export type RolePermissionGrants = string[];

export type TokenPayload = {
  access_token: string;
  refresh_token: string;
  user: {
    name: string;
  };
  permissions?: RolePermissionGrants;
};

const setAccessToken = (access_token: string) => {
  localStorage.setItem('access_token', access_token);
};

const setTokens = (tokenObj: TokenPayload) => {
  localStorage.setItem('access_token', tokenObj.access_token);
  localStorage.setItem('refresh_token', tokenObj.refresh_token);
  localStorage.setItem('user', JSON.stringify(tokenObj.user));
  localStorage.setItem('permissions', JSON.stringify(tokenObj.permissions ?? []));
};

const getAccessToken = () => {
  return localStorage.getItem('access_token');
};

const getRefreshToken = () => {
  return localStorage.getItem('refresh_token');
};

const getUserName = (): string => {
  const stored = localStorage.getItem('user');
  if (!stored) {
    return '';
  }
  return JSON.parse(stored).name;
};

const getUserPermissions = (): RolePermissionGrants => {
  const stored = localStorage.getItem('permissions');
  if (!stored) {
    return [];
  }

  try {
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : [];
  } catch {
    return [];
  }
};

const hasPermission = (key: string): boolean => getUserPermissions().includes(key);

const LIST_COLUMN_VISIBILITY_KEY = 'list_column_visibility';

const readListColumnStore = (): Record<string, Record<string, boolean>> => {
  const stored = localStorage.getItem(LIST_COLUMN_VISIBILITY_KEY);
  if (!stored) {
    return {};
  }

  try {
    const parsed = JSON.parse(stored);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }

    const store: Record<string, Record<string, boolean>> = {};
    Object.entries(parsed).forEach(([listKey, overrides]) => {
      if (!overrides || typeof overrides !== 'object' || Array.isArray(overrides)) {
        return;
      }
      const next: Record<string, boolean> = {};
      Object.entries(overrides).forEach(([columnId, hidden]) => {
        if (typeof hidden === 'boolean') {
          next[columnId] = hidden;
        }
      });
      store[listKey] = next;
    });
    return store;
  } catch {
    return {};
  }
};

const getListColumnVisibility = (listKey: string): Record<string, boolean> => {
  return readListColumnStore()[listKey] ?? {};
};

const setListColumnVisibility = (listKey: string, overrides: Record<string, boolean>) => {
  const store = readListColumnStore();
  store[listKey] = overrides;
  localStorage.setItem(LIST_COLUMN_VISIBILITY_KEY, JSON.stringify(store));
};

const clearListColumnVisibility = () => {
  localStorage.removeItem(LIST_COLUMN_VISIBILITY_KEY);
};

const clearTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('permissions');
  clearListColumnVisibility();
};

export default {
  setTokens,
  setAccessToken,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  getUserPermissions,
  hasPermission,
  getUserName,
  getListColumnVisibility,
  setListColumnVisibility,
  clearListColumnVisibility
};
